import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _kebabCase from 'lodash/kebabCase';
import { getWalletPixallMetadata } from 'reaxl-analytics';
import _capitalize from 'lodash/capitalize';
import { Google, Pixall } from '../analytics/index.js';
import { getInventoryOwnerById, isVDP } from '../utilities/index.js';
import { inventoryTransformer } from '../transformers/pixall/index.js';
import { inventoryTransformer as googleInventoryTransformer } from '../transformers/google/index.js';
import { valueEventCode, nativeDealValues } from '../constants/index.js';
import { getConsumerMetadata } from '../transformers/pixall/DataLayerTransformer';
import { updatePaymentsInfoFormatHelper, updateInputValueHelper } from './helpers/index.js';
const isContinueOnlineCTA = buttonText => buttonText.toLowerCase() === 'continue online';
const isNextCalculateTaxesAndFeeCTA = buttonText => buttonText.toLowerCase() === 'next: calculate taxes & fees';
const {
  applicationData,
  nativeDealEventValues
} = nativeDealValues;

/**
 * @summary Sends Pixall/Google Click event when CTAs on the NDS calculator are clicked
 * @data cmp, listingType, listingId
 * @events event1
 */

export async function ndsCalcCtaClick(_ref, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref;
  if (option === void 0) {
    option = {};
  }
  // eslint-disable-next-line camelcase
  const {
    buttonText,
    listingId,
    cmp = 'vw_dlr_fin_opts',
    pageType,
    paymentsDuck,
    co_txt_url,
    eventResult,
    isSDPlead,
    activeTab,
    financePayment,
    leasePayment,
    downPayment,
    ...rest
  } = data;
  const {
    useCDL = false
  } = option;
  const inventory = getInventoryOwnerById(state, listingId);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const formattedPaymentsInfo = updatePaymentsInfoFormatHelper(paymentsDuck?.selectors?.getDuckState(state), {
    activeTab,
    financePayment,
    leasePayment,
    downPayment
  });
  const sdpLeadType = isSDPlead ? 'continueDealPrimary' : 'continueDealSecondary';
  const payload = {
    cmp,
    par: 'v_lstg',
    zip,
    // eslint-disable-next-line camelcase
    co_txt_url,
    ...rest
  };
  let label = `${cmp} - ${pageId}`;
  let eventResultTemp = eventResult;
  if (isNextCalculateTaxesAndFeeCTA(buttonText)) {
    label = 'cta::next-calculate-taxes-fees::amd';
    eventResultTemp = 'cta::next-calculate-taxes-fees::amd';
  }
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'view',
    feature: 'dr3',
    label,
    listingCode: ownerId,
    pageType,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: useCDL ? {
      eventProperties: {
        event: {
          ...nativeDealEventValues[sdpLeadType],
          elementText: buttonText,
          application: {
            ...applicationData
          },
          ...formattedPaymentsInfo
        }
      }
    } : undefined
  }, dataLayer, option);
  if (eventResult) {
    Google.sendClickEvent({
      eventElement: 'nds',
      eventResult: eventResultTemp
    }, option);
  }

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event when Pencilling on the NDS calculator
 * @data cmp, listingType, listingId
 * @events event1
 */
export function ndsPencilingEvents(_ref2, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref2;
  if (option === void 0) {
    option = {};
  }
  const {
    brand = ''
  } = state;
  const {
    customBrand,
    useCDL = false
  } = option;
  const {
    listingId,
    cmp,
    feature,
    eventName,
    eventResult,
    walletData = {},
    inputName,
    paymentsDuck,
    activeTab = '',
    inputValue,
    ...rest
  } = data;
  const statePaymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const zip = dataLayer?.user?.zipCode || '';
  const linkCopy = event?.target?.innerText || event?.target?.textContent;
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  let label = cmp;

  // Handle the label of the taxes & fees cta on the unified dealstarter
  if (customBrand === 'myWallet' && ['tf_see_dtls', 'see_dtls'].includes(label)) {
    label = 'g_tax_fee';
  }
  const payload = {
    cmp: label,
    par: 'v_lstg',
    zip,
    co_txt_url: linkCopy,
    ...rest
  };
  if (brand === 'kbb') {
    payload.linkName = linkCopy;
  }
  const cdlData = useCDL ? {
    eventProperties: {
      event: {
        ...nativeDealEventValues[inputName],
        ...(inputName === 'taxesAndFee' && {
          elementText: linkCopy
        }),
        deal: {
          paymentMethod: _capitalize(activeTab),
          ...updateInputValueHelper({
            inputName,
            inputValue,
            category: 'deal'
          })
        },
        tradeIn: {
          source: statePaymentsInfo?.tradeIn?.source?.toLowerCase(),
          ...updateInputValueHelper({
            inputName,
            inputValue,
            category: 'tradeIn'
          })
        }
      }
    }
  } : undefined;
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    label,
    listingCode: ownerId,
    feature,
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    tradeinMetadata,
    dealMetadata,
    cdl: cdlData
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventAction: 'activity',
    eventResult,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    },
    ...(eventName && {
      eventName
    })
  }, option);
  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event only onces when DropDowns or Inputs on the NDS calculator are penciled
 * @data listingId, feature
 * @events event1
 */
export function ndsCalcInteraction(_ref3, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref3;
  if (option === void 0) {
    option = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    listingId,
    feature,
    consumerMetadata,
    walletData = {},
    elementText,
    paymentsDuck,
    activeTab,
    financePayment,
    leasePayment,
    inputName,
    inputValue,
    ...rest
  } = data;
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const payload = {
    cmp: 'nds_true',
    par: 'v_lstg',
    zip,
    ...rest
  };
  const formattedPaymentsInfo = updatePaymentsInfoFormatHelper(paymentsDuck?.selectors?.getDuckState(state), {
    activeTab,
    financePayment,
    leasePayment
  });
  const cdlData = useCDL ? {
    eventProperties: {
      event: {
        ...nativeDealEventValues.udsComponentPencil,
        valueEventCode: 'F638',
        elementText,
        gtmEvent: 'pencil',
        ...formattedPaymentsInfo,
        deal: {
          ...formattedPaymentsInfo.deal,
          ...updateInputValueHelper({
            inputName,
            inputValue,
            category: 'deal'
          })
        },
        tradeIn: {
          ...formattedPaymentsInfo.tradeIn,
          ...updateInputValueHelper({
            inputName,
            inputValue,
            category: 'tradeIn'
          })
        }
      }
    }
  } : {
    eventProperties: {
      event: {
        valueEventCode: 'F638'
      }
    }
  };
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    label: 'nds_pencil',
    listingCode: ownerId,
    feature,
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    consumerMetadata,
    tradeinMetadata,
    dealMetadata,
    cdl: cdlData
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dr',
    eventResult: 'pencil::nds',
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Pixall/Google Click event when dealer financing CTA are clicked
 * @data cmp, listingType, listingId
 * @events event1,event23
 */
export async function ndsDealerFinancingCTA(_ref4, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref4;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    cmp,
    ...rest
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const zip = dataLayer?.user?.zipCode || '';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const payload = {
    cmp,
    par: 'v_lstg',
    zip,
    ...rest
  };
  Pixall.sendClickEvent({
    eventSource: 'dealership',
    feature: 'Drive the 5',
    label: `Click to Dealer Site - ${cmp} - ${pageId}`,
    listingCode: ownerId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: {
      eventProperties: {
        event: {
          valueEventCode: '0AAD'
        }
      }
    }
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'dealer',
    eventResult: 'sitelink::nds',
    eventData: {
      vehicle: {
        dealershipId: ownerId?.toString()
      }
    },
    vehicle: {
      cj_ga_event_value_code: '0AAD' // CD166
    }
  }, option);

  // return data for custom event
  return payload;
}

/**
 * @summary Sends Google and Pixall Impressions event on NDS in view impression
 * @data listingId
 */
export function ndsInviewImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId,
    paymentsInfo,
    initialDealType
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  Google.sendImpressions(metadata, {
    eventElement: 'dr',
    eventResult: 'inview::nds',
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  const vehicleObject = inventoryTransformer.getCDLVehicleTransformer({
    ...googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    })
  });
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const cdlData = useCDL ? {
    eventProperties: {
      event: {
        ...nativeDealEventValues.udsComponentView,
        deal: {
          paymentMethod: _capitalize(initialDealType)
        },
        tradeIn: {
          source: paymentsInfo?.tradeIn?.source?.toLowerCase()
        }
      }
    }
  } : {
    eventProperties: {
      vehicle: {
        ...vehicleObject
      }
    }
  };
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'dealership',
    eventType: 'impression',
    label: 'inview::nds',
    pageId,
    vehicleType: inventory?.listingType || '',
    clickType: inventory?.clickType,
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'nds_calculator' : 'nds',
    ...inventoryTransformer.getInventoryData(inventory),
    cdl: cdlData,
    ...(isPrivateSeller && {
      eventSource: 'calculator',
      feature: 'psx',
      listingType: 'pvt',
      pageId: 'fyc_psx_vdp'
    })
  }, option);
  return {};
}

/**
 * @summary Sends Google Impressions event on InsuranceSponsor in view impression
 */
export function ndsInsuranceSponsorInviewImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId,
    sponsor,
    ownerId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    eventSource: 'insurance',
    feature: 'nds',
    label: `insurance_${sponsor.toLowerCase()}-insurance_nds`,
    clickType: inventory.clickType,
    listingCode: ownerId,
    ...inventoryTransformer.getInventoryData(inventory)
  }, option);
  Google.sendImpressions(metadata, {
    eventElement: 'partner',
    eventResult: `insurance::${sponsor.toLowerCase()}-insurance::get-a-quote-for-this-vehicle-in-under-5-minutes::get-a-quote`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google Click event event on InsuranceSponsor CTA click
 */
export function ndsInsuranceSponsorCtaClick(_ref5, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref5;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    sponsor,
    ownerId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  const sponsorName = sponsor?.toLowerCase();
  Pixall.sendClickEvent({
    eventSource: 'insurance',
    feature: 'nds',
    label: `cta_insurance_${sponsorName}-insurance_get-a-quote_nds`,
    listingCode: ownerId,
    pixallData: {
      clickTypeToListingType: true
    },
    inventory,
    vendor: `${sponsorName}-insurance`
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'partner',
    eventResult: `cta::insurance::${sponsorName}-insurance::nds::get-a-quote`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall Impressions event on NDS eligible impression
 * @data listingId
 */
export function ndsEligibleImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    state,
    dataLayer = {},
    history
  } = metadata;
  const inventory = getInventoryOwnerById(state, data.listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const vehicleObject = inventoryTransformer.getCDLVehicleTransformer({
    ...googleInventoryTransformer(inventory, {
      target: 'vehicle',
      dataLayer
    })
  });
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'dealership',
    eventType: 'impression',
    label: 'eligible::nds',
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'nds_calculator' : 'nds',
    pageId,
    pageType: 'vdp',
    listingCode: ownerId,
    clickType: inventory.clickType,
    ...inventoryTransformer.getInventoryData(inventory),
    cdl: {
      eventProperties: {
        vehicle: {
          ...vehicleObject
        }
      }
    },
    ...(isPrivateSeller && {
      eventSource: 'calculator',
      feature: 'psx',
      listingType: 'pvt',
      pageId: 'fyc_psx_vdp'
    })
  }, option);
  Google.sendImpressions(metadata, {
    eventElement: 'dr',
    eventResult: 'eligible::nds',
    eventData: {
      vehicle: {
        dealershipId: ownerId.toString()
      }
    }
  }, option);
  return {};
}

/**
 * @summary Sends Google Click event when tabs are changed
 * @events event1
 */
export function ndsNavigationClick(_ref6, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref6;
  if (option === void 0) {
    option = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    eventResult,
    cmp,
    listingId,
    walletData = {},
    paymentsDuck,
    key
  } = data;
  const {
    paymentsInfo,
    userModified,
    price,
    offerPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, userModified, price, offerPrice);
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const formattedPaymentsInfo = updatePaymentsInfoFormatHelper(paymentsDuck?.selectors?.getDuckState(state), {
    activeTab: key
  });
  const cdlData = useCDL ? {
    eventProperties: {
      event: {
        ...nativeDealEventValues.buildYourToggle,
        elementText: _capitalize(key),
        deal: {
          paymentMethod: formattedPaymentsInfo?.deal?.paymentMethod,
          termLength: formattedPaymentsInfo?.deal?.termLength
        },
        tradeIn: {
          condition: formattedPaymentsInfo?.tradeIn?.condition,
          source: formattedPaymentsInfo?.tradeIn?.source
        }
      }
    }
  } : undefined;
  Google.sendClickEvent({
    eventElement: 'nds',
    eventAction: 'activity',
    eventResult,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'click',
    label: cmp,
    listingCode: ownerId,
    pageId,
    feature: inventory?.paymentServices?.digitalRetailingType === 'accelerate' ? 'dr3' : 'mywallet',
    inventory,
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    tradeinMetadata,
    dealMetadata,
    cdl: cdlData
  }, dataLayer, option);
  return {
    cmp: eventResult
  };
}
export function ndsLendingOptionsCTAClick(_ref7, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref7;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    enableCreditApp,
    isWestlakeEnabled = false,
    placement = 'nds',
    uiContext,
    vendor,
    paymentsDuck,
    activeTab
  } = data;
  const {
    useCDL = false
  } = option;
  const statePaymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const {
    innerText,
    textContent,
    parentNode: {
      innerText: parentNodeInnerText
    } = {}
  } = event?.target || {};
  const linkCopy = (innerText || textContent || parentNodeInnerText || '').trim();
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isSaCa = enableCreditApp && !isAccelerate;
  const isExpFin = linkCopy === 'Explore Financing';
  const isApplyForFinancing = linkCopy === 'Apply for Financing';
  let partnerPrefix = isApplyForFinancing ? 'caca-' : '';
  let partner;
  let label = isExpFin ? 'exp_fin' : 'get_prequal';
  let feature = 'caca';
  let eventElement = 'nds';
  let eventSource = 'dealership';
  let applicationType;
  let calculatorType;
  let hasListingType;
  let leadType;
  let customValueEventCode;
  let gaVehicle;

  // precedence order: accelerate > saca > westlake
  if (isAccelerate) {
    partner = 'amd'; // caca should be only for accelerate or amd dealers.
    feature = 'dr3';
    eventElement = 'accelerate';
    partnerPrefix = 'caca-';
  } else if (isSaCa) {
    partner = 'saca';
  } else if (isWestlakeEnabled) {
    partner = 'westlake';
    customValueEventCode = valueEventCode.WESTLAKE;
  } else if (isExpFin && isPrivateSeller) {
    partner = 'amd';
    feature = 'psx';
    partnerPrefix = 'caca-';
    eventSource = 'link';
  }
  if (partner && (isExpFin && isWestlakeEnabled || !isExpFin)) {
    label += `_${partner}`;
  }
  if (uiContext === 'tile' || uiContext === 'lenderTile') {
    applicationType = 'loan';
    eventSource = 'application';
    label = 'get_loan_expl_fin';
    calculatorType = 'payment';
    hasListingType = true;
  }
  if (isApplyForFinancing) {
    label += `-${placement}`;
    eventSource = 'lead';
    calculatorType = 'payment';
    leadType = 'credit';
    customValueEventCode = valueEventCode.CREDIT_APP;
    gaVehicle = {
      cj_ga_event_value_code: customValueEventCode
    };
  }
  Pixall.sendClickEvent({
    eventSource,
    feature,
    label,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    applicationType,
    calculatorType,
    vendor,
    hasListingType,
    leadType,
    ...(customValueEventCode && {
      cdl: {
        eventProperties: {
          event: {
            valueEventCode: customValueEventCode,
            ...(isApplyForFinancing && useCDL && {
              ...nativeDealEventValues.applyForFinancing,
              elementText: linkCopy,
              application: {
                ...applicationData
              },
              deal: {
                paymentMethod: _capitalize(activeTab)
              },
              gtmEvent: 'credit_application',
              tradeIn: {
                source: statePaymentsInfo?.tradeIn?.source?.toLowerCase()
              }
            })
          }
        }
      }
    })
  }, dataLayer, option);
  if (partner) {
    Google.sendClickEvent({
      eventElement,
      eventResult: `text-link::${partnerPrefix}${partner}::${_kebabCase(linkCopy)}::${placement}`,
      eventNonInteractive: false,
      eventData: {
        vehicle: googleInventoryTransformer(inventory, {
          target: 'vehicle',
          dataLayer
        })
      },
      vehicle: gaVehicle
    }, option);
  }
  return {};
}
export function ndsGetPreQualifiedExploreFinancingClick(_ref8, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref8;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const inventory = getInventoryOwnerById(state, listingId);
  Pixall.sendClickEvent({
    eventSource: 'dealership',
    feature: 'caca',
    label: 'get_prequal_explore_financing',
    inventory
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult: 'text-link::caca-saca::get-pre-qualified-explore-financing'
  }, option);
  return {};
}
export function ndsOfferBreakDownClick(_ref9, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref9;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    tabName,
    walletData = {},
    paymentsDuck
  } = data;
  const {
    customBrand = '',
    useCDL = false
  } = option;
  const inventory = getInventoryOwnerById(state, listingId);
  const isPrivateSeller = inventory?.owner?.privateSeller;
  const isUDS = customBrand === 'myWallet';
  const {
    paymentsInfo,
    price,
    offerPrice
  } = walletData;
  const statePaymentsInfo = paymentsDuck?.selectors?.getDuckState(state) || {};
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', price, offerPrice);
  const pixallPayload = {
    ...(!isPrivateSeller && {
      pixallData: {
        clickTypeToListingType: true
      }
    }),
    ...(isPrivateSeller && {
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    }),
    eventSource: isPrivateSeller ? 'application' : 'dealership',
    feature: isPrivateSeller ? 'psx' : 'caca',
    label: `dropdown::offer-breakdown::${tabName}`,
    inventory,
    tradeinMetadata,
    dealMetadata,
    cdl: useCDL ? {
      eventProperties: {
        event: {
          ...nativeDealEventValues.offerBreakdown,
          deal: {
            paymentMethod: _capitalize(tabName)
          },
          tradeIn: {
            source: statePaymentsInfo?.tradeIn?.source?.toLowerCase()
          }
        }
      }
    } : undefined
  };

  // To handle UnifiedDealStarter brand
  if (isUDS) {
    pixallPayload.calculatorType = 'payment';
    pixallPayload.eventSource = 'calculator';
    pixallPayload.eventType = 'step';
    pixallPayload.feature = 'MyWallet';
  }
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'nds',
    eventResult: `dropdown::offer-breakdown::${tabName}`,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }
  }, option);
  return {};
}
export function ndsKBBTradeInCTAClick(_ref0, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref0;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    paymentsDuck,
    activeTab,
    financePayment,
    leasePayment
  } = data;
  const {
    customBrand = '',
    useCDL = false
  } = option;
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  const linkCopy = event?.target?.innerText || event?.target?.textContent;
  const isUDS = customBrand === 'myWallet';
  const formattedPaymentsInfo = updatePaymentsInfoFormatHelper(paymentsDuck?.selectors?.getDuckState(state), {
    activeTab,
    financePayment,
    leasePayment
  });
  const pixallPayload = {
    eventSource: 'calculator',
    eventType: 'click',
    label: 'val_v_kbb',
    listingCode: ownerId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    },
    cdl: useCDL ? {
      eventProperties: {
        event: {
          ...nativeDealEventValues.tradeInLink,
          elementText: linkCopy,
          ...formattedPaymentsInfo
        }
      }
    } : undefined
  };

  // To handle UnifiedDealStarter brand
  if (isUDS) {
    pixallPayload.label = 'val_v_kbb';
    pixallPayload.calculatorType = 'payment';
    pixallPayload.eventSource = 'calculator';
    pixallPayload.eventType = 'step';
    pixallPayload.feature = 'MyWallet';
  }
  Pixall.sendClickEvent(pixallPayload, dataLayer, option);
  return {};
}
export function ndsEsntialBuyOnlineClick(_ref1, option) {
  let {
    data = {},
    dataLayer,
    event,
    state
  } = _ref1;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    ctaLocation
  } = data;
  let {
    placement = ''
  } = data;
  const {
    brand = ''
  } = state;

  // Backwards compatibility for ctaLocation
  // Please use placement for new implementations
  if (ctaLocation && !placement) {
    placement = ctaLocation;
  }
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isEsntial = !!_get(inventory, 'esntial', false);
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const isShopNowCTA = buttonText => buttonText === 'Shop Now';
  const pageId = isShopNowCTA(textContent) ? 'srp_buy-online_oem' : _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  let eventSource = brand === 'kbb' ? 'calculator' : 'vehicle';
  let eventElement = 'nds';
  let eventResult = `lead::esntial::${placement}`;
  let cmp = textContent.replace(/\s+/g, '_').toLowerCase();
  let label = eventResult;
  let customValueEventCode;
  if (isAccelerate) {
    cmp = 'pur_now_ecomm_dplnk';
    eventElement = 'dealer';
    eventResult = `sitelink::ecomm-deeplink::purchase-now::${placement}`;
    eventSource = 'dealership';
    label = `Click to Dealer Site - ${cmp} - ${pageId}`;
  }
  if (isEsntial) {
    eventElement = 'dealer';
    eventSource = isShopNowCTA(textContent) ? 'vehicle' : 'dealership';
    eventResult = `sitelink::ecomm-${isAccelerate ? 'amd' : 'deeplink'}::start-purchase-now::${placement}`;
    if (isShopNowCTA(textContent)) {
      label = 'cta::shop-now';
    } else {
      label = `pur_now_ecomm_${isAccelerate ? 'amd' : 'dplnk'}::${placement}`;
    }
    customValueEventCode = 'RT42';
    if (isContinueOnlineCTA(textContent)) {
      eventElement = 'nds';
      label = `Click to Dealer Site - lead::esntial::${placement}`;
      eventResult = `lead::esntial::${placement}`;
      customValueEventCode = 'RT42';
    }
  }
  Google.sendClickEvent({
    eventElement,
    eventResult,
    eventName: 'ecomm',
    eventNonInteractive: false,
    eventData: {
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    },
    ...(customValueEventCode && {
      vehicle: {
        cj_ga_event_value_code: customValueEventCode
      }
    })
  }, option);
  if (isContinueOnlineCTA(textContent) || isAccelerate || isEsntial) {
    Pixall.sendClickEvent({
      eventSource,
      label,
      inventory,
      feature: 'Drive the 5',
      pixallData: {
        clickTypeToListingType: isVDP(dataLayer)
      },
      ...(customValueEventCode && {
        cdl: {
          eventProperties: {
            event: {
              valueEventCode: customValueEventCode
            }
          }
        }
      })
    }, dataLayer, option);
  }
  return {
    cmp
  };
}
export function ndsPayoffQuoteCTAClick(_ref10, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref10;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'click',
    label: 'get_payoff_qt',
    listingCode: ownerId,
    pageId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  return {};
}
export function ndsVerifyCreditCTAClick(_ref11, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref11;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId
  } = data;
  const pageId = _get(dataLayer, 'page.brandPg') || _get(dataLayer, 'page.pg', '');
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const ownerId = _get(inventory, 'owner.id') || data.ownerId || '';
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'click',
    feature: isAccelerate ? 'dr3' : 'MyWallet',
    label: 'text-link::verify-my-credit',
    listingCode: ownerId,
    pageId,
    inventory,
    pixallData: {
      clickTypeToListingType: isVDP(dataLayer)
    }
  }, dataLayer, option);
  return {};
}
export async function ndsAutoFill(_ref12, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref12;
  if (option === void 0) {
    option = {};
  }
  const {
    listingId,
    walletData = {},
    paymentsDuck,
    activeTab,
    financePayment,
    leasePayment,
    downPayment
  } = data;
  const {
    paymentsInfo,
    price,
    offerPrice
  } = walletData;
  const consumerMetadata = await getConsumerMetadata();
  if (!_isEmpty(consumerMetadata)) {
    consumerMetadata.walletId = paymentsInfo?.walletId;
  }
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', price, offerPrice);
  const inventory = getInventoryOwnerById(state, listingId);
  const ownerId = _get(inventory, 'owner.id') || '';
  const {
    useCDL = false
  } = option;
  const formattedPaymentsInfo = updatePaymentsInfoFormatHelper(paymentsDuck?.selectors?.getDuckState(state), {
    activeTab,
    financePayment,
    leasePayment,
    downPayment
  });
  Pixall.sendClickEvent({
    eventSource: 'calculator',
    eventType: 'autofill',
    calculatorType: 'payment',
    label: 'autofill_pencil',
    feature: 'MyWallet',
    consumerMetadata,
    dealMetadata,
    tradeinMetadata,
    pixallData: {
      clickTypeToListingType: true
    },
    listingCode: ownerId,
    inventory,
    cdl: useCDL ? {
      eventProperties: {
        event: {
          element: 'Autofill',
          action: 'Pencil',
          application: {
            name: 'Unified Deal Starter',
            solution: 'Retail360'
          },
          tagIndex: 'uds-018',
          ...formattedPaymentsInfo
        }
      }
    } : undefined
  }, dataLayer, option);
  return {};
}
export function ndsSelectButtonInteraction(_ref13, option) {
  let {
    data = {},
    dataLayer,
    state
  } = _ref13;
  const {
    cmp
  } = data;
  const inventoryId = dataLayer.vehicle?.id;
  const inventory = getInventoryOwnerById(state, inventoryId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const isPrivateSeller = inventory?.owner?.privateSeller;
  let eventResult;
  if (cmp === 'edt_pr') {
    eventResult = 'listing-price';
  }
  Pixall.sendClickEvent({
    calculatorType: 'payment',
    eventType: 'step',
    eventSource: 'calculator',
    feature: isAccelerate ? 'dr3' : 'MyWallet',
    inventory,
    label: cmp,
    ...(isPrivateSeller ? {
      feature: 'psx',
      pageId: 'fyc_psx_vdp',
      listingType: 'pvt'
    } : {
      pixallData: {
        clickTypeToListingType: true
      }
    })
  }, dataLayer, option);
  if (eventResult) {
    Google.sendClickEvent({
      eventAction: 'activity',
      eventElement: 'nds',
      eventResult,
      vehicle: googleInventoryTransformer(inventory, {
        target: 'vehicle',
        dataLayer
      })
    }, option);
  }
  return {};
}

/**
 * @summary Sends Pixall Impressions event on NDS calculator start
 * @data listingId, walletData
 */
export async function ndsStartCalculatorImpression(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  if (option === void 0) {
    option = {};
  }
  const {
    data = {},
    dataLayer = {},
    state,
    history
  } = metadata;
  const {
    listingId,
    walletData
  } = data;
  const {
    customBrand = ''
  } = option;
  const isUDS = customBrand === 'myWallet';
  const inventory = getInventoryOwnerById(state, listingId);
  const isAccelerate = _get(inventory, 'paymentServices.digitalRetailingType', '') === 'accelerate';
  const {
    paymentsInfo,
    listingPrice
  } = walletData;
  const {
    dealMetadata,
    tradeinMetadata
  } = getWalletPixallMetadata(paymentsInfo, '', listingPrice);
  let feature;
  // To handle UnifiedDealStarter brand
  if (isUDS) {
    feature = 'can_dealertrack_dr';
  } else {
    feature = isAccelerate ? 'dr3' : 'caca';
  }
  Pixall.sendImpression({
    dataLayer,
    history
  }, {
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'start',
    label: 'loan',
    clickType: inventory?.clickType,
    feature,
    dealMetadata,
    tradeinMetadata,
    ...inventoryTransformer.getInventoryData(inventory)
  }, option);
  return {};
}

/**
 * @summary Sends Pixall CDL Click event when Pixall is loaded successfully (no need to be inview)
 * @data listingId, feature
 * @events event1
 */
export function ndsComponentLoad(_ref14, option) {
  let {
    data = {},
    dataLayer
  } = _ref14;
  if (option === void 0) {
    option = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    paymentsInfo = {}
  } = data;
  if (useCDL) {
    Pixall.sendClickEvent({
      cdl: {
        eventProperties: {
          event: {
            ...nativeDealEventValues.udsComponetLoad,
            tradeIn: {
              source: paymentsInfo?.tradeIn?.source?.toLowerCase()
            }
          }
        }
      }
    }, dataLayer, option);
  }
  return {};
}

/**
 * @summary Sends Pixall CDL Click event when user click anywhere within the UDS the first time
 * @data listingId, feature
 * @events event1
 */
export function ndsInitialClick(_ref15, option) {
  let {
    data = {},
    dataLayer
  } = _ref15;
  if (option === void 0) {
    option = {};
  }
  const {
    useCDL = false
  } = option;
  const {
    paymentsInfo = {},
    activeTab
  } = data;
  if (useCDL) {
    Pixall.sendClickEvent({
      cdl: {
        eventProperties: {
          event: {
            ...nativeDealEventValues.udsComponentInitialClick,
            deal: {
              paymentMethod: _capitalize(activeTab)
            },
            tradeIn: {
              source: paymentsInfo?.tradeIn?.source?.toLowerCase()
            }
          }
        }
      }
    }, dataLayer, option);
  }
  return {};
}
export default {
  ndsAutoFill,
  ndsCalcCtaClick,
  ndsCalcInteraction,
  ndsDealerFinancingCTA,
  ndsPencilingEvents,
  ndsOfferBreakDownClick,
  ndsInviewImpression,
  ndsEligibleImpression,
  ndsNavigationClick,
  ndsLendingOptionsCTAClick,
  ndsEsntialBuyOnlineClick,
  ndsKBBTradeInCTAClick,
  ndsInsuranceSponsorInviewImpression,
  ndsInsuranceSponsorCtaClick,
  ndsPayoffQuoteCTAClick,
  ndsSelectButtonInteraction,
  ndsStartCalculatorImpression,
  ndsComponentLoad,
  ndsInitialClick
};